import { getFieldErrors, isApiError } from '@/services/api';
import type { MotorplanPaymentValues } from '../schemas/motorplanPayment.schema';
import type { PaymentLinkSummary } from '../types/paymentLinks.types';
import { readPaymentLinkSummary } from '../services/paymentLinks';
import { hasJustificationRequiredCode } from './accessRefusal';

export const MOTORPLAN_UNAVAILABLE_MESSAGE =
    'MotorPlan Payments did not confirm the payment link. It may still have reached the customer; check with them before sending another.';
export const MOTORPLAN_FAILED_MESSAGE =
    'The payment link could not be confirmed as sent. It may still have reached the customer; check with them before sending another.';
export const MOTORPLAN_JUSTIFICATION_REQUIRED_MESSAGE =
    'Your recorded reason for accessing this Agreement has expired. Record it again, then send the payment link.';

const FIELD_MESSAGES: Record<string, { field: keyof MotorplanPaymentValues; message: string }> = {
    amount_below_minimum: { field: 'amountGross', message: 'The amount is below the minimum MotorPlan can take.' },
    amount_settles_agreement: {
        field: 'amountGross',
        message: 'The amount is too close to what is left to pay for a part payment.',
    },
    customer_email_missing: { field: 'sendViaEmail', message: 'No email address is held for this customer.' },
    customer_phone_missing: { field: 'sendViaSms', message: 'No mobile number is held for this customer.' },
};

const FORM_MESSAGES: Record<string, string> = {
    nothing_outstanding: 'The customer has paid in full',
    pay_off_not_available: "This plan can't be paid off by link",
    agreement_not_eligible: 'MotorPlan cannot take a payment link for this agreement.',
    link_limit_reached: 'Too many payment links were sent for this agreement recently. Try again later.',
    agreement_not_payable: "This plan's state does not allow this payment link.",
    payment_link_in_progress: 'Another payment link is being sent for this agreement. Wait a moment, then try again.',
};

const REQUEST_FIELDS: Record<string, keyof MotorplanPaymentValues> = {
    amount_gross: 'amountGross',
    send_via_email: 'sendViaEmail',
    send_via_sms: 'sendViaSms',
};

export interface MotorplanSubmitErrors {
    fieldErrors: Partial<Record<keyof MotorplanPaymentValues, string>>;
    formError: string | null;
    liveLink?: PaymentLinkSummary;
}

const formOnly = (formError: string): MotorplanSubmitErrors => ({ fieldErrors: {}, formError });

const liveLinkOf = (error: { data?: unknown }): PaymentLinkSummary | null => {
    const body = error.data;
    return typeof body === 'object' && body !== null && 'live_link' in body
        ? readPaymentLinkSummary((body as Record<string, unknown>).live_link)
        : null;
};

function validationErrors(error: { message: string }): MotorplanSubmitErrors {
    const fieldErrors: MotorplanSubmitErrors['fieldErrors'] = {};
    let unmapped = false;
    for (const [field, messages] of Object.entries(getFieldErrors(error))) {
        const formField = Object.hasOwn(REQUEST_FIELDS, field) ? REQUEST_FIELDS[field] : undefined;
        if (formField && messages[0]) fieldErrors[formField] = messages[0];
        else unmapped = true;
    }
    const noFieldMapped = Object.keys(fieldErrors).length === 0;
    return { fieldErrors, formError: unmapped || noFieldMapped ? error.message : null };
}

export function motorplanSubmitErrors(error: unknown): MotorplanSubmitErrors {
    if (hasJustificationRequiredCode(error)) return formOnly(MOTORPLAN_JUSTIFICATION_REQUIRED_MESSAGE);
    if (!isApiError(error)) return formOnly(MOTORPLAN_FAILED_MESSAGE);
    if (error.status === 503) return formOnly(MOTORPLAN_UNAVAILABLE_MESSAGE);

    const code = error.code ?? '';
    const fieldMessage = Object.hasOwn(FIELD_MESSAGES, code) ? FIELD_MESSAGES[code] : undefined;
    if (fieldMessage) return { fieldErrors: { [fieldMessage.field]: fieldMessage.message }, formError: null };

    if (Object.hasOwn(FORM_MESSAGES, code)) return formOnly(FORM_MESSAGES[code]);
    if (error.status === 409 && code === 'live_link_exists') {
        const liveLink = liveLinkOf(error);
        return liveLink
            ? { fieldErrors: {}, formError: null, liveLink }
            : formOnly(MOTORPLAN_FAILED_MESSAGE);
    }
    if (error.status === 422) return validationErrors(error);

    return formOnly(MOTORPLAN_FAILED_MESSAGE);
}
